// Ported from CDK

import React from 'react';
import { Landmark, CreditCard, TriangleAlert, Merge, CloudOff } from "lucide-react";
import { getPaymentMethodVendor } from "@common";

import { theme } from "../../tailwind.theme";
const { colors } = theme.extend;

// Inlined PaymentTypeCode enum
enum PaymentTypeCode {
    ACH = 1,
    Card
}

interface PaymentMethodBadgeProps {
    paymentType?: PaymentTypeCode;
    sourceName?: string | null;
    multiplePaymentTypes?: boolean;
    offlinePayments?: boolean;
}

interface IconConfig {
    width: number;
    position?: `bottomRight`;
}

type IconConfigKey = `checking` | `multiplePayments` | `card` | `alert` | `offline` | `visa` | `apple` | `master` | `amex` | `jcb` | `google` | `discover` | `diner`;

const ICON_CONFIGS: Record<IconConfigKey, IconConfig> = {
    checking: { width: 12 },
    multiplePayments: { width: 12 },
    card: { width: 12 },
    alert: { width: 12 },
    offline: { width: 12 },
    visa: { width: 19.6 },
    apple: { width: 20.053 },
    master: { width: 18.412 },
    amex: { width: 23.213 },
    jcb: { width: 16.149 },
    google: { width: 20 },
    discover: { width: 24.706, position: `bottomRight` },
    diner: { width: 14.082 }
};

export const PaymentMethodBadge: React.FC<PaymentMethodBadgeProps> = ({
    paymentType,
    sourceName,
    multiplePaymentTypes,
    offlinePayments
}) => {
    let icon: React.ReactElement<React.SVGProps<SVGSVGElement>>;
    let background: string;
    let iconConfig: IconConfig;

    if (multiplePaymentTypes) {
        icon = <Merge />;
        background = colors[`neutral`][50];
        iconConfig = ICON_CONFIGS.multiplePayments;
    } else if (offlinePayments) {
        icon = <CloudOff />;
        background = colors[`orange`][100];
        iconConfig = ICON_CONFIGS.offline;
    } else if (!paymentType) {
        icon = <TriangleAlert />;
        background = colors[`red`][100];
        iconConfig = ICON_CONFIGS.alert;
    } else {
        const vendor = getPaymentMethodVendor(sourceName);
        const isACH = paymentType === PaymentTypeCode.ACH;

        if (isACH) {
            icon = <Landmark />;
            background = colors[`neutral`][50];
            iconConfig = ICON_CONFIGS.checking;
        } else if (vendor) {
            const VendorIcon = vendor.icon;
            icon = <VendorIcon />;
            background = vendor.color !== `neutral` ? vendor.color : colors[`neutral`][50];
            iconConfig = ICON_CONFIGS[vendor.key] || ICON_CONFIGS.card;
        } else {
            icon = <CreditCard />;
            background = colors[`neutral`][50];
            iconConfig = ICON_CONFIGS.card;
        }
    }

    // Tailwind classes equivalent to original CSS module
    // Original:
    //   width: 28px, height: 20px, border-radius: 4px, display: flex,
    //   align-items: center, justify-content: center
    const containerClass = iconConfig.position === `bottomRight`
        ? `w-7 h-5 rounded flex items-end justify-end`
        : `w-7 h-5 rounded flex items-center justify-center`;

    const containerStyle: React.CSSProperties = {
        backgroundColor: background
    };

    const iconStyle: React.CSSProperties = {
        width: `${iconConfig.width}px`
    };

    const iconElement = React.isValidElement(icon)
        ? React.cloneElement(icon, { style: iconStyle, className: `h-auto` })
        : icon;

    return (
        <div className={containerClass} style={containerStyle}>
            {iconElement}
        </div>
    );
};
